/**
 * Read More Toggle - Custom element for expandable text content
 * Truncates content based on character limits and provides a toggle button
 */
if (!customElements.get('read-more-toggle')) {
  class ReadMoreToggle extends HTMLElement {
    constructor() {
      super();
      this.content = null;
      this.button = null;
      this.buttonText = null;
      this.fullContent = '';
      this.fullText = '';
      this.isExpanded = false;
      this.mobileLimit = 100;
      this.desktopLimit = 200;
      this.breakpoint = 768;
      this.handleToggle = this.handleToggle.bind(this);
      this.handleResize = this.debounce(this.checkTruncation.bind(this), 150);
    }

    connectedCallback() {
      this.content = this.querySelector('.product-description-content');
      this.button = this.querySelector('.product-description-toggle-btn');
      this.buttonText = this.button?.querySelector('.product-description-toggle-btn__text');

      if (!this.content || !this.button) {
        console.warn('ReadMoreToggle: Missing content or button element');
        return;
      }

      this.mobileLimit = parseInt(this.dataset.mobileLimit, 10) || 100;
      this.desktopLimit = parseInt(this.dataset.desktopLimit, 10) || 200;
      
      // Store the full content only on first initialization
      // (prevents overwriting with truncated content if element is moved in DOM)
      if (!this.fullContent) {
        this.fullContent = this.content.innerHTML;
        this.fullText = this.content.textContent.trim();
      }

      this.init();
    }

    disconnectedCallback() {
      if (this.button) {
        this.button.removeEventListener('click', this.handleToggle);
      }
      window.removeEventListener('resize', this.handleResize);
    }

    init() {
      this.checkTruncation();
      this.button.addEventListener('click', this.handleToggle);
      window.addEventListener('resize', this.handleResize);
    }

    /**
     * Debounce utility
     */
    debounce(fn, delay) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    }

    /**
     * Get the current character limit based on viewport
     */
    getLimit() {
      return window.innerWidth < this.breakpoint ? this.mobileLimit : this.desktopLimit;
    }

    /**
     * Escape HTML entities to prevent XSS when inserting text via innerHTML
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Truncate text to a character limit, trying to break at word boundary
     */
    truncateText(text, limit) {
      if (text.length <= limit) return text;
      
      // Find the last space before the limit
      let truncated = text.substring(0, limit);
      const lastSpace = truncated.lastIndexOf(' ');
      
      if (lastSpace > limit * 0.5) {
        truncated = truncated.substring(0, lastSpace);
      }
      
      return truncated.trim() + '...';
    }

    /**
     * Check if content should be truncated and update UI
     */
    checkTruncation() {
      const limit = this.getLimit();
      const needsTruncation = this.fullText.length > limit;

      if (needsTruncation) {
        this.button.style.removeProperty('display');
        this.button.style.display = 'inline-flex';
        
        if (!this.isExpanded) {
          // Apply text truncation (escape to prevent XSS)
          const truncatedText = this.truncateText(this.fullText, limit);
          this.content.innerHTML = '<p>' + this.escapeHtml(truncatedText) + '</p>';
          this.content.classList.add('truncated');
        }
      } else {
        this.button.style.display = 'none';
        this.content.innerHTML = this.fullContent;
        this.content.classList.remove('truncated');
      }
    }

    /**
     * Handle toggle button click
     * @param {Event} evt - Click event
     */
    handleToggle(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      
      this.isExpanded = !this.isExpanded;

      if (this.isExpanded) {
        this.expand();
      } else {
        this.collapse();
      }
    }

    /**
     * Expand the content
     */
    expand() {
      this.content.innerHTML = this.fullContent;
      this.content.classList.remove('truncated');
      this.button.setAttribute('aria-expanded', 'true');
      this.classList.add('is-expanded');

      if (this.buttonText) {
        this.buttonText.textContent = this.button.dataset.readLess;
      }
    }

    /**
     * Collapse the content
     */
    collapse() {
      const limit = this.getLimit();
      const truncatedText = this.truncateText(this.fullText, limit);
      
      // Escape to prevent XSS
      this.content.innerHTML = '<p>' + this.escapeHtml(truncatedText) + '</p>';
      this.content.classList.add('truncated');
      this.button.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-expanded');

      if (this.buttonText) {
        this.buttonText.textContent = this.button.dataset.readMore;
      }

      // Scroll back to top of content if needed
      const rect = this.getBoundingClientRect();
      if (rect.top < 0) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  customElements.define('read-more-toggle', ReadMoreToggle);
}
